import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Grid, TextField, MenuItem, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, Paper, Typography, Tooltip, InputAdornment, IconButton, Chip, Alert
} from "@mui/material";
import { Search, Clear, CheckCircle, GraphicEq, Refresh, RestartAlt } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  aiCallApi, useAICallCampaigns, formatBdt, formatDuration, formatNumber, dhaka, dhakaDate, bdDateString,
  endReasonText, OUTCOMES, OUTCOME_KEYS, PURPOSES, PURPOSE_KEYS
} from "./useAICalling";
import { AICallingPage, Loading, ErrorState, OutcomeChip, CallStateChip, PurposeChip, ConfirmDialog } from "./AICallingComponents";

// "older": also show the calls before the fresh start (hidden, never deleted)
const FILTER_KEYS = ["from", "to", "campaignId", "outcome", "intent", "paid", "q", "purpose", "older"];
const PAGE_SIZES = [25, 50, 100];

const AICallingCalls = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { campaigns, fetch: fetchCampaigns } = useAICallCampaigns();

  const query = params.toString();
  const filters = useMemo(() => {
    const p = new URLSearchParams(query);
    return Object.fromEntries(FILTER_KEYS.map((k) => [k, p.get(k) || ""]));
  }, [query]);
  const page = Math.max(1, parseInt(new URLSearchParams(query).get("page"), 10) || 1);
  const limitParam = parseInt(new URLSearchParams(query).get("limit"), 10);
  const limit = PAGE_SIZES.includes(limitParam) ? limitParam : 25;

  // text boxes apply on Enter / Search
  const [text, setText] = useState({ q: filters.q, intent: filters.intent });
  useEffect(() => {
    setText({ q: filters.q, intent: filters.intent });
  }, [filters.q, filters.intent]);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [history, setHistory] = useState({ from: null, olderHidden: 0 });
  const [confirmFresh, setConfirmFresh] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await aiCallApi.calls({ ...filters, page, limit });
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setError("");
    setRows(Array.isArray(res.data) ? res.data : []);
    setPagination(res.body?.pagination || { total: 0, totalPages: 1 });
    setHistory({ from: res.body?.historyFrom || null, olderHidden: res.body?.olderHidden || 0 });
  }, [filters, page, limit]);

  const freshStart = async (clear = false) => {
    const res = await aiCallApi.callsFreshStart(clear);
    if (!res.ok) return toast.error(res.error);
    toast.success(res.message || "Done");
    setConfirmFresh(false);
    setParams(new URLSearchParams());
    load();
  };

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  /** Change URL params (filters reset the page). */
  const update = (changes, keepPage = false) => {
    const next = new URLSearchParams(query);
    for (const [k, v] of Object.entries(changes)) {
      if (v === "" || v === null || v === undefined) next.delete(k);
      else next.set(k, String(v));
    }
    if (!keepPage) next.delete("page");
    setParams(next);
  };
  const setFilter = (key) => (e) => update({ [key]: e.target.value });
  const applyText = () => update({ q: text.q.trim(), intent: text.intent.trim() });
  const clearAll = () => setParams(new URLSearchParams());
  const hasFilters = FILTER_KEYS.some((k) => filters[k]);

  const quickRange = (days) => update({ from: bdDateString(-(days - 1)), to: bdDateString(0) });

  return (
    <AICallingPage
      title="Call History"
      subtitle="Every AI call with its outcome, cost and transcript"
      actions={
        <>
          <Button variant="outlined" startIcon={<RestartAlt />} onClick={() => setConfirmFresh(true)}>Start fresh</Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
        </>
      }
    >
      {history.from && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            filters.older ? (
              <Button color="inherit" size="small" onClick={() => update({ older: "" })}>Hide older</Button>
            ) : history.olderHidden > 0 ? (
              <Button color="inherit" size="small" onClick={() => update({ older: "1" })}>Show older calls</Button>
            ) : undefined
          }
        >
          {filters.older
            ? `Showing all calls, including the ones before the fresh start on ${dhaka(history.from)}.`
            : `Fresh start: showing calls since ${dhaka(history.from)}.${history.olderHidden ? ` ${formatNumber(history.olderHidden)} older calls are hidden (not deleted).` : ""}`}
        </Alert>
      )}
      <ConfirmDialog
        open={confirmFresh}
        title="Start the call history fresh?"
        confirmText="Start fresh"
        onConfirm={() => freshStart(false)}
        onClose={() => setConfirmFresh(false)}
      >
        <Typography variant="body2">
          Every call before now is hidden from this list, so you only see new calls. Nothing is deleted: "Show older calls" brings them
          back, and Reports, follow-ups, the daily budget and the call rules still use them.
        </Typography>
        {history.from && (
          <Button size="small" sx={{ mt: 1 }} onClick={() => freshStart(true)}>Or show the whole history again</Button>
        )}
      </ConfirmDialog>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" placeholder="Name or mobile"
              value={text.q}
              onChange={(e) => setText((t) => ({ ...t, q: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && applyText()}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                endAdornment: text.q ? (
                  <IconButton size="small" onClick={() => { setText((t) => ({ ...t, q: "" })); update({ q: "" }); }} aria-label="Clear search">
                    <Clear fontSize="small" />
                  </IconButton>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="From" value={filters.from} onChange={setFilter("from")} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="To" value={filters.to} onChange={setFilter("to")} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Chip size="small" label="Today" onClick={() => quickRange(1)} clickable />
            <Chip size="small" label="7 days" onClick={() => quickRange(7)} clickable />
            <Chip size="small" label="30 days" onClick={() => quickRange(30)} clickable />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField select fullWidth size="small" label="Campaign" value={filters.campaignId} onChange={setFilter("campaignId")}>
              <MenuItem value="">All</MenuItem>
              {campaigns.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
              {filters.campaignId && !campaigns.some((c) => c._id === filters.campaignId) && (
                <MenuItem value={filters.campaignId}>This campaign</MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select fullWidth size="small" label="Purpose" value={filters.purpose} onChange={setFilter("purpose")}>
              <MenuItem value="">All</MenuItem>
              {PURPOSE_KEYS.map((k) => <MenuItem key={k} value={k}>{PURPOSES[k]}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select fullWidth size="small" label="Outcome" value={filters.outcome} onChange={setFilter("outcome")}>
              <MenuItem value="">All</MenuItem>
              {OUTCOME_KEYS.map((k) => <MenuItem key={k} value={k}>{OUTCOMES[k].label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              fullWidth size="small" label="Intent"
              value={text.intent}
              onChange={(e) => setText((t) => ({ ...t, intent: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && applyText()}
              placeholder="e.g. wants_package"
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select fullWidth size="small" label="Paid" value={filters.paid} onChange={setFilter("paid")}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3} md={1} sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={applyText} fullWidth>Search</Button>
          </Grid>
        </Grid>
        {hasFilters && (
          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Button size="small" startIcon={<Clear />} onClick={clearAll}>Clear filters</Button>
            {filters.paid && (
              <Typography variant="caption" color="text.secondary">
                The paid filter checks the latest 2,000 matching calls. Paid = paid within the attribution window after the call.
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {error && !rows.length ? (
        <ErrorState error={error} onRetry={load} />
      ) : loading && !rows.length ? (
        <Loading text="Loading calls..." />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1100, opacity: loading ? 0.6 : 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell align="right">Talk time</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="center">Link sent</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="center">Recording</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      {hasFilters ? "No calls match these filters." : "No calls yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((c) => {
                    const why = c.outcome === "blocked" || c.outcome === "failed" ? endReasonText(c.endReason) : "";
                    return (
                      <TableRow key={c._id} hover className="aic-row-link" onClick={() => navigate(`/ai-calling/calls/${c._id}`)}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{dhaka(c.createdAt)}</TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography variant="body2" noWrap title={c.name}>{c.name || "—"}</Typography>
                          {c.intent && <Typography variant="caption" color="text.secondary" noWrap display="block">{c.intent}</Typography>}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{c.mobile}</TableCell>
                        <TableCell><PurposeChip purpose={c.purpose} /></TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography variant="body2" noWrap title={c.campaignName}>{c.campaignName || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          {c.outcome ? (
                            <Tooltip title={why || ""}><span><OutcomeChip outcome={c.outcome} /></span></Tooltip>
                          ) : (
                            <CallStateChip state={c.state} />
                          )}
                          {why && <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 180 }}>{why}</Typography>}
                        </TableCell>
                        <TableCell align="right">{c.answeredAt ? formatDuration(c.durationSec) : "—"}</TableCell>
                        <TableCell align="right">{formatBdt(c.costBdt)}</TableCell>
                        <TableCell align="center">{c.linkSent ? <CheckCircle fontSize="small" color="success" titleAccess="Link sent" /> : ""}</TableCell>
                        <TableCell align="right">
                          {c.paid ? (
                            <Tooltip title={`Paid ${dhakaDate(c.paid.at)}`}>
                              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "success.main", whiteSpace: "nowrap" }}>
                                <CheckCircle fontSize="small" /> {formatBdt(c.paid.amount)}
                              </Box>
                            </Tooltip>
                          ) : ""}
                        </TableCell>
                        <TableCell align="center">{c.hasRecording ? <GraphicEq fontSize="small" color="action" titleAccess="Has a recording" /> : ""}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={Math.min(page - 1, Math.max(0, (pagination.totalPages || 1) - 1))}
            onPageChange={(_, p) => update({ page: p + 1 }, true)}
            rowsPerPage={limit}
            rowsPerPageOptions={PAGE_SIZES}
            onRowsPerPageChange={(e) => update({ limit: e.target.value })}
            labelRowsPerPage="Rows"
            labelDisplayedRows={({ from, to, count }) => `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(count)}`}
          />
        </Paper>
      )}
    </AICallingPage>
  );
};

export default AICallingCalls;
